import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Printer, Monitor, Apple } from "lucide-react";

const WINDOWS_STEPS = [
    'Press the Windows key on your keyboard and type "Mouse settings".',
    'Click "Mouse settings", then click "Additional mouse settings" on the right.',
    'In the pop-up, click the "Pointers" tab.',
    'Under "Scheme", choose "Windows Black (extra large)" or "Windows Inverted (extra large)".',
    'Click "Apply", then "OK". Your pointer will now be bigger and easier to see.',
    'Extra tip: In Settings > Accessibility > Mouse pointer, drag the size slider up and pick a bright colour.',
];

const MAC_STEPS = [
    'Click the Apple menu (top-left corner) and choose "System Settings".',
    'Click "Accessibility" in the sidebar, then click "Display".',
    'Scroll to "Pointer". Drag the "Pointer size" slider to the right.',
    'Choose a "Pointer outline colour" (black) and a bright "Pointer fill colour" (orange or green).',
    'Wiggle your mouse or trackpad and enjoy your bright, easy-to-see pointer!',
];

export default function PrintGuidePage() {
    return (
        <div className="print-guide min-h-screen bg-white">
            {/* Screen-only header + print button (hidden when printing) */}
            <header className="no-print max-w-4xl mx-auto px-6 md:px-10 pt-8 pb-4 flex items-center justify-between">
                <Link to="/" className="btn-pill" data-testid="back-home">
                    <ArrowLeft size={18} /> Back to ClearCursor
                </Link>
                <button
                    type="button"
                    onClick={() => window.print()}
                    className="btn-pill solid"
                    data-testid="print-btn"
                >
                    <Printer size={18} /> Print this page
                </button>
            </header>

            <main className="max-w-4xl mx-auto px-8 md:px-12 py-10 md:py-14 print:py-6">
                <div className="border-b-4 border-slate-900 pb-6 mb-8">
                    <div className="text-sm uppercase tracking-widest font-bold text-slate-700">
                        ClearCursor · setup sheet
                    </div>
                    <h1 className="font-display font-extrabold text-5xl md:text-6xl text-slate-900 mt-2 leading-tight">
                        Make Your Mouse Pointer Bigger &amp; Brighter
                    </h1>
                    <p className="text-xl text-slate-800 mt-4 max-w-3xl">
                        A one-page guide for you or someone you care about. Follow the
                        steps for the computer you use.
                    </p>
                </div>

                <section className="mb-12 print:break-inside-avoid">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-14 h-14 border-2 border-slate-900 flex items-center justify-center rounded-2xl bg-slate-900 text-white">
                            <Monitor size={28} />
                        </div>
                        <h2 className="font-display font-extrabold text-3xl md:text-4xl text-slate-900">
                            On a Windows computer
                        </h2>
                    </div>
                    <ol className="list-decimal pl-8 space-y-4 text-xl leading-relaxed text-slate-900">
                        {WINDOWS_STEPS.map((s, i) => <li key={i}>{s}</li>)}
                    </ol>
                </section>

                <section className="print:break-before-page">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-14 h-14 border-2 border-slate-900 flex items-center justify-center rounded-2xl bg-slate-900 text-white">
                            <Apple size={28} />
                        </div>
                        <h2 className="font-display font-extrabold text-3xl md:text-4xl text-slate-900">
                            On a Mac computer
                        </h2>
                    </div>
                    <ol className="list-decimal pl-8 space-y-4 text-xl leading-relaxed text-slate-900">
                        {MAC_STEPS.map((s, i) => <li key={i}>{s}</li>)}
                    </ol>
                </section>

                <footer className="mt-14 pt-6 border-t-2 border-slate-900 text-slate-700 text-base">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                            For a bright mouse pointer <em>right now</em>, visit{" "}
                            <strong className="text-slate-900">clearcursor.app</strong>
                        </div>
                        <div className="text-sm">© 2026 Christopher T. Parker</div>
                    </div>
                </footer>
            </main>
        </div>
    );
}
